import { useMemo } from 'react';

import { cn } from '@crossnokaye/ux-common';

import { ButtonComponentActionConfigVariant, DropdownOption } from '@repo/sdk/models/components';

import { analytics } from '@/analytics/analytics-service';
import { DropdownCell, type DropdownCellOption } from '@/components/data-table';
import { getRefValue, mapLegacyButtonVariantToClassName } from '@/features/equipment-browser/utils';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';

import { ActionIcon } from './action-icon';
import { ActionComponentProps } from './types';
import { useHandleUpdateSettings } from './use-handle-update-settings';

/** Cell option carrying the SDK actions needed to apply a selection. */
type ActionDropdownOption = DropdownCellOption & {
  actions?: DropdownOption['actions'];
};

export const ActionDropdown = ({
  state,
  disabled,
  analyticsAttributes,
  className,
  ...buttonProps
}: ActionComponentProps) => {
  const { isDisabled: disabledByPermissions, tooltipText: permissionTooltipText } =
    useActionPermission({
      // TODO: UX-3355: We probably need to specify permissions in the dropdown config
      privilege: PRIVILEGES.writeCommands,
    });

  const { handleUpdateSettings, isPending } = useHandleUpdateSettings();

  const dropdownConfig = state?.actionComponentConfig?.dropdownComponentActionConfig;

  const currentValue = dropdownConfig?.valueRef
    ? getRefValue(dropdownConfig.valueRef)?.toString()
    : undefined;

  const handleSelect = (option: ActionDropdownOption) => {
    const currentLabel =
      dropdownConfig?.options?.find((dropdownOption) => dropdownOption.value === currentValue)
        ?.label ?? currentValue;

    analytics.track('Device Mode Changed', {
      ...analyticsAttributes,
      'Previous Device Mode': currentLabel,
      'Device Mode': option.label,
    });

    handleUpdateSettings(option.actions?.settingActions || []);
  };

  // Every option carries its own icon, and the menu always shows it — picking a
  // mode from a list of unlabelled glyphs is how an operator recognises it. The
  // config's `showIcon` governs the trigger alone, which the BFF sets only for the
  // details header; the table and card triggers stay text-only.
  const options = useMemo<ActionDropdownOption[]>(
    () =>
      (dropdownConfig?.options ?? []).map((option) => ({
        value: option.value,
        label: option.label,
        actions: option.actions,
        icon: option.icon?.icon ? (
          <ActionIcon
            icon={option.icon.icon}
            variant={option.icon.variant}
            fallbackIcon="ShieldQuestion"
          />
        ) : undefined,
      })),
    [dropdownConfig?.options]
  );

  const disabledButton = isPending || dropdownConfig?.disabled || disabled;
  const disabledActions = disabledByPermissions;

  const buttonVariant = dropdownConfig?.buttonVariant ?? ButtonComponentActionConfigVariant.Default;

  // View Only wins: system conditions don't matter until the operator goes active.
  const tooltipContent = disabledByPermissions
    ? permissionTooltipText
    : dropdownConfig?.disabled
      ? 'The fault cannot be reset until system conditions are met.'
      : undefined;

  return (
    <DropdownCell<ActionDropdownOption>
      {...buttonProps}
      className={cn(mapLegacyButtonVariantToClassName(buttonVariant), className)}
      disabled={disabledButton}
      disableItems={disabledActions}
      onSelect={handleSelect}
      options={options}
      showTriggerIcon={dropdownConfig?.showIcon ?? false}
      stateName={state?.stateName}
      subtitle={dropdownConfig?.subtitle}
      tooltipContent={tooltipContent}
      value={currentValue}
    />
  );
};
